import { io, type ManagerOptions, type Socket, type SocketOptions } from 'socket.io-client';

import { QCP_INVALID_TICKET, type QcpHandshakeAuth } from '@/lib/qcp/protocol';
import { qcpRestFailureOf } from '@/lib/qcp/rest';
import { qcpWsUrlAmmesso, type QcpTicketGrant, type QcpTicketPurpose } from '@/lib/qcp/ticket';

// Il socket di QChinesePoker col TICKET del BFF di Limulo (T1a; condizioni U4-U6 di tma-orcb). UNA funzione apre il socket per i
// tre hook (`useQcpMatch`, `useCpQueue`, `useCpInvite`): nessun altro file chiama `io(` per QCP.
//
// 🔴 Il ticket e MONOUSO (30 s): ogni connessione e OGNI riconnessione ne chiede uno NUOVO. `reconnection: false` apposta — con
// `reconnection: true` socket.io ripresenta lo stesso `auth` e il secondo handshake e `invalid_ticket`. Il ticket sta SOLO
// nell'`auth` della stretta di mano: mai in un URL, mai in uno storage, mai in un log o in un errore (i motivi sono una lista chiusa).
// Un ticket rifiutato (`invalid_ticket`: scaduto, gia usato) si ritenta UNA volta con un ticket nuovo; poi `onFail` e il «Riprova»
// di chi guarda. L'indirizzo e il `wsUrl` della risposta, in lista chiusa (`qcpWsUrlAmmesso`): nessun ripiego su `env.qcpWsUrl`.

/** Perche una connessione non c'e: sono TUTTI i motivi, e nessuno porta un valore (un ticket, un indirizzo, un messaggio grezzo). */
export type QcpConnFail = 'non-disponibile' | 'ticket' | 'indirizzo' | 'stretta-di-mano' | 'caduta';

/** Dopo una caduta si riconnette con un ticket nuovo, ma non all'infinito: al tetto, `caduta` e il «Riprova» di chi guarda. */
export const QCP_RICONNESSIONI_MAX = 3;

export interface QcpSocketOpts {
  purpose: QcpTicketPurpose;
  /** Chiede UN ticket nuovo al BFF; `null` = la risposta non l'aveva. Lancia un errore RTK se il BFF rifiuta. */
  issueTicket: (purpose: QcpTicketPurpose) => Promise<QcpTicketGrant | null>;
  /** Per OGNI socket aperto (anche dopo una riconnessione): chi guarda ci attacca i suoi ascoltatori. */
  onSocket: (socket: Socket) => void;
  onFail: (motivo: QcpConnFail) => void;
  /** Per i banchi: il socket finto. */
  connect?: (url: string, opts: Partial<ManagerOptions & SocketOptions>) => Socket;
}

/** Apre il socket; rende la chiusura (smontaggio, «Esci»): ferma tutto e non lascia un socket aperto. */
export function openQcpSocket(o: QcpSocketOpts): () => void {
  const connect = o.connect ?? io;
  let chiuso = false;
  let corrente: Socket | null = null;
  let ritentatoPerTicket = false;
  let riconnessioni = 0;

  const fallisci = (motivo: QcpConnFail): void => {
    if (chiuso) return;
    chiuso = true;
    corrente?.close();
    corrente = null;
    o.onFail(motivo);
  };

  async function tentativo(): Promise<void> {
    let grant: QcpTicketGrant | null;
    try {
      grant = await o.issueTicket(o.purpose);
    } catch (e) {
      // La STESSA regola del REST (`lib/qcp/rest.ts`): «non disponibile» SOLO per `424 E_QLASH_CAPABILITY_DISABLED` (la capability e spenta).
      // Un `E_QLASH_CAPABILITY_RESOLVER_ERROR` (o qualunque altro 424) e un GUASTO: errore con Riprova.
      fallisci(qcpRestFailureOf(e) === 'non-disponibile' ? 'non-disponibile' : 'ticket');
      return;
    }
    if (chiuso) return; // chiuso mentre si aspettava: il ticket non si usa
    if (grant === null) return fallisci('ticket');
    if (!qcpWsUrlAmmesso(grant.wsUrl)) return fallisci('indirizzo'); // fuori lista: il ticket non esce dal telefono
    const s = connect(grant.wsUrl, { auth: { ticket: grant.ticket } satisfies QcpHandshakeAuth, transports: ['websocket'], reconnection: false, forceNew: true });
    corrente = s;
    s.on('connect', () => {
      if (corrente === s) ritentatoPerTicket = false;
    });
    s.on('connect_error', (e: Error) => {
      if (chiuso || corrente !== s) return;
      s.close();
      if (e?.message === QCP_INVALID_TICKET && !ritentatoPerTicket) {
        ritentatoPerTicket = true; // UN nuovo tentativo, con un ticket nuovo
        void tentativo();
      } else fallisci('stretta-di-mano');
    });
    s.on('disconnect', (motivo: string) => {
      if (chiuso || corrente !== s || motivo === 'io client disconnect') return;
      if (riconnessioni >= QCP_RICONNESSIONI_MAX) return fallisci('caduta');
      riconnessioni += 1;
      void tentativo(); // riconnessione = ticket NUOVO
    });
    o.onSocket(s);
  }

  void tentativo();
  return () => {
    chiuso = true;
    corrente?.close();
    corrente = null;
  };
}
